import type { DesignSystemRef, SurfaceDecision } from './kernel-contract.js';
import { ENGINE_UNREACHABLE } from './boot-error.js';

/**
 * CAOS-61 — the PRODUCTION decision source: a thin same-origin fetch of the kernel's boot
 * endpoint (`boot/surface`). The host is the pixel layer; the kernel is the pixel-free DATA
 * source. This asks the kernel WHICH surface to render for the current request and hands the
 * emitted {@link SurfaceDecision} straight to the {@link SurfaceHost} — the host adds no
 * resolution logic of its own (the kernel owns the boot resolution order).
 *
 * The kernel answers with the substrate's passthrough envelope — `{ ok: true, data, meta }` on
 * success, `{ ok: false, error }` on failure — so the decision is the `data` field. The
 * `boot/surface` route is unauthenticated by design: with no session the kernel returns the
 * `sign-in` decision rather than a 401, which is exactly the surface a fresh, unauthenticated
 * visitor should see.
 *
 * The `?surface=` fixture path stays available for local verification behind a dev flag — see
 * `main.ts`; this module is the real seam it stands in for.
 */

/**
 * The kernel boot endpoint, relative to the host origin. The host proxies the kernel API under
 * the `/api` prefix (CAOS-196 BFF, `host/src/worker.ts`), same-origin — matching the sign-in
 * surface's `/api/auth/login`; the worker strips `/api` before the kernel sees `boot/surface`.
 */
export const BOOT_SURFACE_PATH = '/api/boot/surface';

/** The kernel's baked-default brand seam — the fallback design when the kernel is unreachable. */
const BAKED_DEFAULT_DESIGN_SYSTEM: DesignSystemRef = {
  key: 'caos-default',
  source: 'default',
  version: 0,
  tokens: {
    '--brand-accent': '#00969B',
    '--brand-accent-strong': '#016E75',
    '--brand-accent-soft': '#E4F4F4',
    '--brand-mark': '"◆"',
    '--brand-wordmark': '"CAOS"',
    '--brand-os-suffix': '"CLOUD ATLANTIS OS"',
    '--brand-ai-app-name': '"Atlantis AI"',
  },
};

/** The substrate passthrough envelope, as the host reads it off the wire. */
type BootEnvelope =
  | { ok: true; data: SurfaceDecision }
  | { ok: false; error: { message?: string; code?: string } };

/**
 * An `error` {@link SurfaceDecision} the host can always render when the kernel could not be
 * reached or answered a non-decision (a transport failure, not a boot-path condition the kernel
 * itself modelled). Fail-soft: the visitor sees a legible error surface, never a blank page.
 *
 * CAOS-662 — it carries the same envelope an Engine-decided error does, from the workspace's own
 * normalizer (`boot-error.ts`), under a code the catalogue names. The technical detail stays on
 * the issue; the screen renders the envelope.
 */
function unreachableDecision(message: string): SurfaceDecision {
  return {
    surface: 'error',
    stage: 'org-resolution',
    issues: [
      {
        path: '/',
        code: ENGINE_UNREACHABLE.code,
        message,
        params: {},
      },
    ],
    design: BAKED_DEFAULT_DESIGN_SYSTEM,
    error: ENGINE_UNREACHABLE,
  };
}

/**
 * Fetch the {@link SurfaceDecision} for the current request from the kernel's `boot/surface`
 * endpoint (same-origin). Returns a fail-soft `error` decision if the kernel is unreachable or
 * the response is not a well-formed success envelope.
 */
export async function fetchKernelDecision(
  path: string = BOOT_SURFACE_PATH,
): Promise<SurfaceDecision> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
      // The boot decision is auth-sensitive: NEVER serve it from the HTTP cache. A cached
      // authenticated decision replayed to a now-unauthenticated reload would hand the workspace to
      // an anonymous visitor. `no-store` forces a fresh, session-accurate answer on every boot.
      cache: 'no-store',
    });
  } catch (err) {
    return unreachableDecision(
      `The host could not reach the kernel boot endpoint (${err instanceof Error ? err.message : String(err)}).`,
    );
  }

  let envelope: BootEnvelope;
  try {
    envelope = (await response.json()) as BootEnvelope;
  } catch {
    return unreachableDecision('The kernel boot endpoint returned a response that was not valid JSON.');
  }

  if (!envelope || envelope.ok !== true) {
    const detail =
      envelope && envelope.ok === false && envelope.error?.message
        ? envelope.error.message
        : `the kernel returned HTTP ${response.status}`;
    return unreachableDecision(`The kernel could not produce a surface decision (${detail}).`);
  }

  return envelope.data;
}
